import { useState, type FormEvent, type ReactElement } from "react";
import { Link } from "react-router-dom";

import { api, ApiError } from "../api/client";
import { AuthShell } from "../components/AuthShell";

// Request a reset link. The API responds the same whether or not the email is
// registered (no account enumeration), so we always show the sent state.
export function ForgotPassword(): ReactElement {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await api<{ detail: string }>("POST", "/auth/password/reset/request", {
        json: { email },
        auth: false,
      });
      setSent(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not send the reset link.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthShell title="Forgot password" subtitle="We'll email you a link to reset it.">
      {sent ? (
        <p className="form-ok">
          If we have an account or study record for that email, a link is on
          its way. Check your inbox.
        </p>
      ) : (
        <form onSubmit={onSubmit} className="auth-form">
          <label className="field">
            <span>Email</span>
            <input
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
            />
          </label>
          {error ? <p className="form-error">{error}</p> : null}
          <button type="submit" className="btn-primary-glow btn-block" disabled={busy}>
            {busy ? "Sending…" : "Send reset link"}
          </button>
        </form>
      )}
      <p className="auth-alt muted">
        Returning from the previous FaceAge website? Enter your old email
        &mdash; the link will restore your account and study data.
      </p>
      <p className="auth-alt muted">
        Signed up with a study ID? Contact your study team for a reset link.
      </p>
      <p className="auth-alt">
        <Link to="/login">Back to sign in</Link>
      </p>
    </AuthShell>
  );
}
